import { Component } from 'vue-property-decorator'
import Vue from '@src/vue'
import { CreateElement } from 'vue'
import camelcaseKeys from 'camelcase-keys'

import { Loader } from '@src/components/loading-screen/loader/loader'

import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import { AffectedProduct, ExerciseSupportTicket, StateRecording, StateTicket } from '@src/data/support-ticket'
import EventBus from '@src/lib/event_bus'

import './exercises-support.sass'
import { convertYYYYMMDDToDate } from '@src/util/date-format'
import { Tab, Tabs } from '@src/components/tabs/tabs'
import { TicketsList } from '@src/routes/support/components/exercises/tickets-list'
import { Ticket } from '@src/routes/support/components/exercises/ticket'
import { Icon } from '@src/elements/icon/icon'
import { Subtitle2 } from '@src/elements/typography/typography'
import { Notifications } from '@src/routes/support/components/exercises/notifications'
import { TicketModal } from '@src/routes/support/components/exercises/ticket-modal'

@Component({
  computed: {
    loadAvailableExercisesComplete () {
      return this.$store.state.loadAvailableExercisesComplete
    }
  }
})
export class ExercisesSupport extends Vue {

  private tickets: ExerciseSupportTicket[] = []

  private apiClient: ApiClient = new ApiClient()
  private loading: boolean = true

  private loadAvailableExercisesComplete: boolean

  private selectedTab: StateTicket

  private scrollTargetId: number | null = null

  private selectedTicketId: number | null | undefined = null

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('SUPPORT.SECTION')}`
    this.scrollIfNecessary()
  }

  setCurrentTab () {
    let basePage = this.$route.path.slice(this.$route.path.indexOf('support-ticket'))
    let index = basePage.indexOf('/')
    if (index < 0) {
      this.selectedTab = StateTicket.DRAFT
      this.$router.push(`/trainers/platform/support/support-ticket/draft`)
      return
    }
    let subPage = basePage.slice(index + 1)
    this.selectedTab = subPage as StateTicket
  }

  created () {
    this.setCurrentTab()

    EventBus.on('reloadSupportTickets', this.loadTickets)
    return this.loadTickets()
  }

  beforeDestroy () {
    EventBus.off('reloadSupportTickets')
  }

  isTabSelected (tab: StateTicket) {
    return this.$route.path.includes(tab)
  }

  selectTab (tab: StateTicket) {
    this.selectedTab = tab
    this.$router.push(`/trainers/platform/support/support-ticket/${tab}`)
  }

  getExtraMessage (h: CreateElement, ticketsWithoutRecording) {
    if (this.selectedTab !== StateTicket.DRAFT) {
      return
    }
    return <div class='ticket-group-draft'>
      <Icon name='util/info' class='info-icon'/>
      <Subtitle2>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.DRAFT_TICKETS_COUNT', { number: ticketsWithoutRecording })}</Subtitle2>
    </div>
  }

  scrollIfNecessary () {
    if (this.scrollTargetId) {
      // @ts-ignore
      document.getElementById(this.scrollTargetId)!.scrollIntoView({ behavior: 'smooth' } as ScrollIntoViewOptions)
      this.scrollTargetId = null
    } else {
      // the list is not there while the tickets load or when there are none
      const body = document.getElementById('content-box-body')
      if (body) body.scrollTop = 0
    }
  }

  navigateToTicket (ticket: ExerciseSupportTicket) {
    this.scrollTargetId = ticket.id
    this.selectedTicketId = ticket.id
    this.selectTab(ticket.state)
  }

  render (h: CreateElement) {
    if (!this.loadAvailableExercisesComplete || this.loading) {
      return <Loader />
    }

    let ticketsWithoutRecording = 0
    let filteredTickets: any[] = []

    this.tickets.forEach((ticket: ExerciseSupportTicket) => {
      if (this.selectedTab === StateTicket.DRAFT && ticket.state === StateTicket.DRAFT) {
        if (ticket.details.stateRecording !== StateRecording.FINISHED_AND_UPLOADED) ticketsWithoutRecording += 1
        filteredTickets.push(<Ticket supportTicket={ticket} position={filteredTickets.length + 1} id={ticket.id} key={ticket.id} />)
      } else if (ticket.state === this.selectedTab) {
        filteredTickets.push(<Ticket supportTicket={ticket} id={ticket.id} key={ticket.id}/>)
      }
    })

    let selectedTicket = this.tickets.find((localTicket) => localTicket.id === this.selectedTicketId)

    return <div class='exercises-support'>
      <div class='component-tabs-wrapper custom-margin'>
        <Tabs>
          <Tab label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.DRAFT_TICKETS')} selected={this.isTabSelected(StateTicket.DRAFT)}
               selectTab={() => this.selectTab(StateTicket.DRAFT)}/>
          <Tab label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.OPEN_TICKETS')} selected={this.isTabSelected(StateTicket.OPEN)}
               selectTab={() => this.selectTab(StateTicket.OPEN)}/>
          <Tab label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.IN_PROGRESS')} selected={this.isTabSelected(StateTicket.IN_PROGRESS)}
               selectTab={() => this.selectTab(StateTicket.IN_PROGRESS)}/>
          <Tab label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.CLOSED_TICKETS')} selected={this.isTabSelected(StateTicket.CLOSED)}
               selectTab={() => this.selectTab(StateTicket.CLOSED)}/>
        </Tabs>
      </div>
      <TicketsList type={this.selectedTab} tickets={filteredTickets} extraMessage={this.getExtraMessage(h, ticketsWithoutRecording)}/>

      <Notifications selectCallback={this.navigateToTicket}/>
      {selectedTicket ? <TicketModal action='edit' supportTicket={selectedTicket} closeCallback={() => this.selectedTicketId = null}/> : null}

    </div>
  }

  private loadTickets () {
    return this.apiClient.get<any>(API.TRAINERS_API.SUPPORT_TICKETS)
      .then(response => {
        this.tickets = response.support_tickets
          .filter(ticket => ticket.problem_type === AffectedProduct.STATION_LEGACY || ticket.problem_type === AffectedProduct.STATION_SOFTWARE)
          .sort((a, b) => {
            let dateA = convertYYYYMMDDToDate(a.date).getTime()
            let dateB = convertYYYYMMDDToDate(b.date).getTime()
            if (dateA === dateB) return b.id - a.id
            return dateB - dateA
          })
          .map(ticket => new ExerciseSupportTicket(camelcaseKeys(ticket, { deep: true })))
      })
      .finally(() => this.loading = false)
  }

}
